import React from 'react'

import * as THREE from 'three'

import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader';
import {RGBELoader} from 'three/examples/jsm/loaders/RGBELoader';

import { modelsData } from '../../../data/models.data';
import { cameraData } from '../../../data/camera.data';

interface Scene {
  canvasRef: any,
}

interface Props {
  isThreeActive: boolean,
  currentAccessoriesSelected: any,
  currentColorSelected: any,
  selectedCar: any,
  handleLoad: any,
  currentIndividualAccessory: any,
  isMobile: any,
  accessoriesMenuMobileOpened: any
}

interface State {
  accesoriesWithCarPaint: any
}

declare const window: any;

class Scene extends React.Component<Props,State>{
  constructor(props: Props){
    super(props);
    this.state = {
      accesoriesWithCarPaint: []
    }

    this.canvasRef = React.createRef()
  }

  componentDidMount(): void {

    window.addEventListener('resize', ()=> {
      this.handleResizeDesktop()
    })

    this.handleCreateScene()
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    window.addEventListener('resize', ()=> {
      this.handleResizeDesktop()
    })

      if(prevProps.currentColorSelected !== this.props.currentColorSelected){
        this.handleChangeColor()
      }
      if(prevProps.currentIndividualAccessory !== this.props.currentIndividualAccessory){
        this.handleMoveCamera()
      } 
      if(prevProps.accessoriesMenuMobileOpened !== this.props.accessoriesMenuMobileOpened){
        this.handleResize()
      }
  }

  handleCreateScene = () => {
    const mountRef = this.canvasRef

    // Scene creation
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0xEDEDED)
    window.scene = scene
    

    // Camera
    const camera = new THREE.PerspectiveCamera(50, mountRef.current.clientWidth / mountRef.current.clientHeight, 0.1, 1000)
    camera.position.set(4,6,12)

    camera.fov = 50;   
    window.camera = camera

    scene.add(camera)

    var clearColor = new THREE.Color(1, 1, 1);

    // Renderer
    const renderer = new THREE.WebGLRenderer({
      canvas: mountRef.current,
      antialias: true
    })

    // Env
    const rgbeLoader = new RGBELoader()
    rgbeLoader.load("./models/hdr/Industrial_Room.hdr", (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      scene.environment = texture

      renderer.toneMapping = THREE.LinearToneMapping;
      renderer.toneMappingExposure = 1;
    })

    // Lights
    const directional = new THREE.DirectionalLight(0xFFE0B5)
    scene.add(directional)
    directional.position.set(-2.533,3.575,-1.244)
    directional.intensity = 1


    const directional2 = new THREE.DirectionalLight(0xC7CCFF)
    scene.add(directional2)
    directional2.position.set(0.266,-1.786,-0.986)
    directional2.intensity = 1


    const directional3 = new THREE.DirectionalLight(0x6CBBFF)
    scene.add(directional3)
    directional3.position.set(2.482,0.913,-2.865)
    directional3.intensity = 1

    

    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight)
    renderer.shadowMap.enabled = true
    renderer.outputEncoding = THREE.LinearEncoding
    //renderer.shadowMap = THREE.PCFSoftShadowMap
    
    renderer.setClearColor(clearColor)
    renderer.setPixelRatio(window.devicePixelRatio)
    renderer.physicallyCorrectLights = true
    renderer.localClippingEnabled = true
    renderer.gammaOutput = true
    renderer.gammaFactor = 2.2

    window.renderer = renderer
    renderer.render(scene,camera)

    //Controls
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.target = new THREE.Vector3(0,0.5,0)
    controls.enableDamping = true
    controls.minDistance = 4;
    controls.maxDistance = 7;
    controls.maxPolarAngle = Math.PI / 2
    window.controls = controls

    this.handleAnimate()
    this.handleLoadModel()
  }

  handleAnimate = () => {
    window.controls.update()
    window.requestAnimationFrame(this.handleAnimate)
    window.renderer.render(window.scene, window.camera)
  }

  handleLoadModel = () => {
    if(modelsData()[0][this.props.selectedCar]){
      this.props.handleLoad(true)

      //load model 3D
      const gltfLoader = new GLTFLoader()
      const dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath('/draco/')
      gltfLoader.setDRACOLoader(dracoLoader)

      let initialAccessoriesSelected: any = []

      gltfLoader.load(modelsData()[0][this.props.selectedCar].model, (gltf: any) => {
        const model = gltf.scene
        model.name = 'car'
        const material = model.getObjectByName('SM_BodyPaint')
        var color = modelsData()[0][this.props.selectedCar]?.colors[0]
        if(material && color){
          material.material = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(color.settings.color).convertSRGBToLinear(),
            emissive: new THREE.Color(color.settings.emissive).convertSRGBToLinear(),
            emissiveIntensity: color.settings.emissiveIntesity,
            fog: true,
            metalness: color.settings.metalness,
            roughness: color.settings.roughness,
            clearcoat: color.settings.clearcoat,
            reflectivity: color.settings.reflectivity,
            clearcoatRoughness: color.settings.clearcoatRoughness,
            clearcoatIntensity: color.settings.clearcoatIntensity,
            side: THREE.FrontSide
          })
        }
        window.scene.add(model)

        var tempAccesoriesWithCarPaint: any = []

        modelsData()[0][this.props.selectedCar]?.accesories.map((accesory: any) => 
          gltfLoader.load(accesory.model, (gltf: any) => {

            const model = gltf.scene
            model.name = accesory.name
            model.visible = accesory.name.includes("BDM")

            if(accesory.name.includes("BDM")){
              initialAccessoriesSelected.push(accesory)
            }
            var color = modelsData()[0][this.props.selectedCar]?.colors[0]

            if(color.stickerColor){
              const stickerMaterial = model.getObjectByName('BDM_StickerColor')
              if(stickerMaterial){
                tempAccesoriesWithCarPaint.push(model)
                stickerMaterial.material = new THREE.MeshStandardMaterial({
                  color: new THREE.Color(color.stickerColor.color).convertSRGBToLinear(),
                  roughness: color.stickerColor.roughness,
                  metalness: color.stickerColor.metalness
                })
              }
              
            }

            const materialBase = model.getObjectByName('BDM_BodyPaint')
            if(materialBase){
              tempAccesoriesWithCarPaint.push(model)
              materialBase.material = new THREE.MeshPhysicalMaterial({
                color: new THREE.Color(color.settings.color).convertSRGBToLinear(),
                emissive: new THREE.Color(color.settings.emissive).convertSRGBToLinear(),
                emissiveIntensity: color.settings.emissiveIntesity,
                metalness: color.settings.metalness,
                roughness: color.settings.roughness,
                clearcoat: color.settings.clearcoat,
                reflectivity: color.settings.reflectivity,
                clearcoatRoughness: color.settings.clearcoatRoughness,
                clearcoatIntensity: color.settings.clearcoatIntensity,
                depthWrite: true,
              })
            }

            const materialAditional = model.getObjectByName('ADM_BodyPaint')
            if(materialAditional){
              tempAccesoriesWithCarPaint.push(model)
              materialAditional.material = new THREE.MeshPhysicalMaterial({
                color: new THREE.Color(color.settings.color).convertSRGBToLinear(),
                emissive: new THREE.Color(color.settings.emissive).convertSRGBToLinear(),
                emissiveIntensity: color.settings.emissiveIntesity,
                metalness: color.settings.metalness,
                roughness: color.settings.roughness,
                clearcoat: color.settings.clearcoat,
                reflectivity: color.settings.reflectivity,
                clearcoatRoughness: color.settings.clearcoatRoughness,
                clearcoatIntensity: color.settings.clearcoatIntensity,
                depthWrite: true,
              })
            }

            window.scene.add(model)
            
          })
        )

        this.setState({
          accesoriesWithCarPaint: tempAccesoriesWithCarPaint
        })

        this.props.handleLoad(false)
        
      })
    }
  }

  handleChangeColor = () => {
    var selectedObject: any = window.scene.getObjectByName("car")
    if(selectedObject){
      const materialBody = selectedObject.getObjectByName('SM_BodyPaint')
      materialBody.material = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(this.props.currentColorSelected.settings.color).convertSRGBToLinear(),
        emissive: new THREE.Color(this.props.currentColorSelected.settings.emissive).convertSRGBToLinear(),
        emissiveIntensity: this.props.currentColorSelected.settings.emissiveIntesity,
        fog: true,
        metalness: this.props.currentColorSelected.settings.metalness,
        roughness: this.props.currentColorSelected.settings.roughness,
        clearcoat: this.props.currentColorSelected.settings.clearcoat,
        clearcoatRoughness: this.props.currentColorSelected.settings.clearcoatRoughness,
        clearcoatIntensity: this.props.currentColorSelected.settings.clearcoatIntensity,
        reflectivity: this.props.currentColorSelected.settings.reflectivity,
        side: THREE.FrontSide,
      })
      
      this.state.accesoriesWithCarPaint.map((accesory: any) => {

        const stickerMaterial = accesory.getObjectByName('BDM_StickerColor')
        if(stickerMaterial){
          stickerMaterial.material = new THREE.MeshStandardMaterial({
            color: new THREE.Color(this.props.currentColorSelected.stickerColor.color).convertSRGBToLinear(),
            roughness: this.props.currentColorSelected.stickerColor.roughness,
            metalness: this.props.currentColorSelected.stickerColor.metalness
          })
        }
        

        const materialBase = accesory.getObjectByName('BDM_BodyPaint')
        if(materialBase){
          materialBase.material = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(this.props.currentColorSelected.settings.color).convertSRGBToLinear(),
            emissive: new THREE.Color(this.props.currentColorSelected.settings.emissive).convertSRGBToLinear(),
            emissiveIntensity: this.props.currentColorSelected.settings.emissiveIntesity,
            metalness: this.props.currentColorSelected.settings.metalness,
            roughness: this.props.currentColorSelected.settings.roughness,
            clearcoat: this.props.currentColorSelected.settings.clearcoat,
            clearcoatRoughness: this.props.currentColorSelected.settings.clearcoatRoughness,
            clearcoatIntensity: this.props.currentColorSelected.settings.clearcoatIntensity,
            depthWrite: true,
          })
        }
        

        const materialAditional = accesory.getObjectByName('ADM_BodyPaint')
        if(materialAditional){
          materialAditional.material = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(this.props.currentColorSelected.settings.color).convertSRGBToLinear(),
            emissive: new THREE.Color(this.props.currentColorSelected.settings.emissive).convertSRGBToLinear(),
            emissiveIntensity: this.props.currentColorSelected.settings.emissiveIntesity,
            metalness: this.props.currentColorSelected.settings.metalness,
            roughness: this.props.currentColorSelected.settings.roughness,
            clearcoat: this.props.currentColorSelected.settings.clearcoat,
            clearcoatRoughness: this.props.currentColorSelected.settings.clearcoatRoughness,
            clearcoatIntensity: this.props.currentColorSelected.settings.clearcoatIntensity,
            depthWrite: true,
          })
        }
        
        return null
      })
    }
  }


  handleMoveCamera = () => {

    //this.handleHide()

    var coordinateX = 0
    var coordinateY = 0
    var coordinateZ = 0

    if(cameraData()[0][this.props.selectedCar] && cameraData()[0][this.props.selectedCar][this.props.currentIndividualAccessory.name]){
      coordinateX = cameraData()[0][this.props.selectedCar][this.props.currentIndividualAccessory.name].x
      coordinateY = cameraData()[0][this.props.selectedCar][this.props.currentIndividualAccessory.name].y
      coordinateZ = cameraData()[0][this.props.selectedCar][this.props.currentIndividualAccessory.name].z
    }

    else {
      coordinateX = window.camera.position.x
      coordinateY = window.camera.position.y
      coordinateZ = window.camera.position.z
    }

    const targetPosition = new THREE.Vector3(coordinateX, coordinateY, coordinateZ)

    const duration = 10000
    const startTime = Date.now()

    const animateCamera = () => {
      const currentTime = Date.now()
      const elapsedTime = currentTime - startTime
      const t = Math.min(elapsedTime / duration, 1)

      const currentPosition = new THREE.Vector3()
      currentPosition.lerpVectors(window.camera.position, targetPosition, t)
      window.camera.position.copy(currentPosition)

      const euler = new THREE.Euler().setFromQuaternion(window.camera.quaternion)

      const quaternion = new THREE.Quaternion().setFromEuler(euler)
      
      if(t < 1 && elapsedTime < 2000 && (targetPosition.x.toFixed(1) !== window.camera.position.x.toFixed(1) || targetPosition.y.toFixed(1) !== window.camera.position.y.toFixed(1) || targetPosition.z.toFixed(1) !== window.camera.position.z.toFixed(1))){
        requestAnimationFrame(animateCamera)
      }
      else {
        this.handleShowAccessories()
      }

      
    }

    animateCamera()

  }

  handleHide = () => {
    Object.keys(this.props.currentAccessoriesSelected).map(async(accessory: any) => {
      var selectedObject = window.scene.getObjectByName(accessory)

      if(selectedObject && this.props.currentAccessoriesSelected[accessory] === 0){
        if(selectedObject.visible){
          selectedObject.visible = false
        }
      }
      return null
    })
  }

  handleShowAccessories = () => {
    Object.keys(this.props.currentAccessoriesSelected).map(async(accessory: any) => {
      var selectedObject = window.scene.getObjectByName(accessory)
      if(selectedObject && this.props.currentAccessoriesSelected[accessory] === 0){
        if(selectedObject.visible){
          selectedObject.visible = false
        }
      }
      
      if(selectedObject && this.props.currentAccessoriesSelected[accessory] === 1){
        if(selectedObject.visible === false){
          selectedObject.visible = true
          selectedObject.traverse((object) => {
            if(object.isMesh){
              object.material.transparent = true
              object.material.alphaTest = 0.3
              object.material.opacity = 0.3
              var opacityLevel = 0.3
              const interval = setInterval(() => {
                opacityLevel += 0.1
                object.material.opacity = opacityLevel
                if(opacityLevel > 1){
                  clearInterval(interval)
                  setTimeout(() => {
                    this.handleBlink(object)
                  }, 500)
                }
              },50)
            }
          })
          
        }
        
      }
      return null
    })
  }

  handleBlink = (payload: any) => {
    var times = 1
    var decreasing = false
    var current = 1
    const timer = setInterval(() => {
      if(times === 0){
        clearInterval(timer)
        var currentOpacity = payload.material.opacity
        const lastTimer = setInterval(() => {
          currentOpacity += 0.1
          payload.material.opacity = currentOpacity
          if(currentOpacity > 1 ){
            clearInterval(lastTimer)
          }
        }, 100)
      }
      else {
        if(current + 0.1 <= 1.1 && decreasing === false){
          current += 0.1
          payload.material.opacity = current
          decreasing = current > 1
        }
        if(current - 0.1 >= 0.48 && decreasing){
          current -= 0.1
          payload.material.opacity = current
          decreasing = current < 0.51 ? false:true
          if(decreasing === false){
            times -= 1
          }
        }
      }
    }, 100)
  }

  handleResize = () => {
    setTimeout(() => {
      if(window.renderer && window.camera && this.canvasRef.current.clientWidth && this.canvasRef.current.clientHeight){
        window.renderer.setSize(this.canvasRef.current.clientWidth, this.canvasRef.current.clientHeight);
      
        window.camera.aspect = this.canvasRef.current.clientWidth / this.canvasRef.current.clientHeight;
        window.camera.updateProjectionMatrix()
      }
      
    }, 400)
  }

  handleResizeDesktop = () => {
    window.renderer.setSize(window.innerWidth, window.innerHeight)
      window.camera.aspect = window.innerWidth / window.innerHeight
      window.camera.updateProjectionMatrix()
  }

  render(): React.ReactNode {
      return(
        <canvas className={this.props.isThreeActive ? 'webgl fade-in':'webgl fade-out'} id={'webgl'} style={{height: this.props.isMobile && this.props.accessoriesMenuMobileOpened === false  ? 'calc(100% - 40vh - 5%)': this.props.isMobile && this.props.accessoriesMenuMobileOpened  ? '85%':'100%'}} ref={this.canvasRef} ></canvas>
      )
  }
}

export default Scene